/**
 * Nora promo — puts the CHF 100 discount CODE on the cart while a sleeper sofa
 * is in it, and takes it off again when the sofa leaves.
 *
 * Deliberately tiny and standalone, and bound at the document level: the cart
 * drawer re-renders its own innerHTML on every change, so anything bound to a
 * node inside it stops existing after the first update. The drawer is a global
 * section, so this file is live on every page — which is the point, since a
 * sofa can be added from a collection card without a PDP ever being opened.
 *
 * WHY A CODE AND NOT A GIFT LINE. A campaign that adds a gift LINE to the
 * product and lets an automatic Buy X Get Y zero it. This campaign is money
 * off, it has to appear in the applied-codes list, and the customer has to be
 * able to remove it — so it is a real code, applied through the same
 * /cart/update.js call the discount field uses.
 *
 * The campaign's rules live in snippets/kc-nora-promo.liquid and reach this
 * file through the [data-kc-nora-promo] node. Nothing about "what is a Nora"
 * is decided here.
 */
(function () {
  "use strict";

  /**
   * Ceiling on cart writes per page load.
   *
   * Not a substitute for the checks below — a circuit breaker behind them. Any
   * disagreement between this script and another writer about whether the code
   * belongs on the cart would otherwise ping-pong for as long as the tab is
   * open, and the BF campaign's history says that is not hypothetical.
   */
  var MAX_WRITES = 3;
  var writes = 0;

  /**
   * The budget is per SOFA EPISODE, not per page. The sofa entering or leaving
   * the cart is the customer acting, not a loop, so it starts a fresh budget —
   * otherwise add, remove, add on one page spent all three writes and the
   * re-added sofa got neither its code nor its timer (Ivan 17/09).
   */
  var lastHasNora = null;

  /**
   * Set when Shopify answers that the code does not stick, and never unset for
   * the life of the page.
   *
   * This is the state the theme is in until the discount is created in the
   * admin. Without it every cart event would spend a POST discovering the same
   * "not applicable" again — three per page load before the ceiling below
   * caught it, on a cart the customer is actively using.
   */
  var standDown = false;

  /** Set once the customer takes the code off themselves. See onRemoveClick. */
  var DISMISS_KEY = "kc:nora-promo-dismissed";

  function routeRoot() {
    var shopify = window.Shopify;
    if (shopify && shopify.routes && shopify.routes.root) return shopify.routes.root;
    return "/";
  }

  /**
   * sessionStorage throws outright in Safari private mode rather than
   * returning null, which is why every access here is wrapped — the same
   * precaution snippets/js-variables.liquid takes for localStorage. A storage
   * failure must not stop the campaign working; it only means the customer's
   * removal is forgotten on the next page.
   */
  function isDismissed() {
    try {
      return window.sessionStorage.getItem(DISMISS_KEY) === "1";
    } catch (error) {
      return false;
    }
  }

  function setDismissed(value) {
    try {
      if (value) {
        window.sessionStorage.setItem(DISMISS_KEY, "1");
      } else {
        window.sessionStorage.removeItem(DISMISS_KEY);
      }
    } catch (error) {
      /* private mode: nothing to do, and nothing worth failing over */
    }
  }

  function readConfig() {
    var node = document.querySelector("[data-kc-nora-promo]");
    if (!node) return null;

    var code = (node.getAttribute("data-code") || "").trim();
    if (!code) return null;

    var types = (node.getAttribute("data-types") || "")
      .split(",")
      .map(function (type) {
        return type.trim().toLowerCase();
      })
      .filter(Boolean);

    if (!types.length) return null;

    var windowHours = parseInt(node.getAttribute("data-window-hours"), 10);
    if (!(windowHours > 0)) windowHours = 24;

    var memoryHours = parseInt(node.getAttribute("data-memory-hours"), 10);
    if (!(memoryHours > 0)) memoryHours = 72;

    return {
      code: code,
      codeKey: code.toLowerCase(),
      types: types,
      windowSeconds: windowHours * 3600,
      memorySeconds: memoryHours * 3600,
    };
  }

  /** The cart attribute the deadline mirrors into. See kc-nora-promo.liquid. */
  var DEADLINE_ATTR = "_nora_deadline";

  /** The durable record. localStorage, not session: it has to outlive the tab. */
  var DEADLINE_KEY = "kc:nora-deadline";

  function nowSeconds() {
    return Math.floor(Date.now() / 1000);
  }

  /**
   * The deadline as the CART currently records it. 0 when absent.
   *
   * Anything unparseable counts as "not started" rather than as expired: a
   * corrupted attribute must not be able to withdraw an offer the customer can
   * see ticking.
   */
  function readCartDeadline(cart) {
    var attributes = (cart && cart.attributes) || {};
    var raw = parseInt(attributes[DEADLINE_ATTR], 10);
    return raw > 0 ? raw : 0;
  }

  /**
   * The deadline this browser was already given, if it still remembers it.
   *
   * Stored as "<deadline>|<issuedAt>". The second half is what makes the
   * memory expire independently of the offer: once `memorySeconds` have passed
   * since it was issued, the record is dropped and a genuinely new visit can
   * be given a fresh window. Until then this value is returned whether the
   * deadline is still running OR already lapsed — returning an expired
   * deadline is precisely how re-adding the sofa is stopped from minting a new
   * one.
   *
   * localStorage throws outright in Safari private mode rather than returning
   * null, hence the wrapper. A storage failure degrades to the cart attribute
   * alone, which is the old behaviour: the offer still works, it is just
   * restartable.
   */
  function readStoredDeadline(config) {
    var raw;
    try {
      raw = window.localStorage.getItem(DEADLINE_KEY);
    } catch (error) {
      return 0;
    }
    if (!raw) return 0;

    var parts = String(raw).split("|");
    var deadline = parseInt(parts[0], 10);
    var issuedAt = parseInt(parts[1], 10);
    if (!(deadline > 0) || !(issuedAt > 0)) {
      clearStoredDeadline();
      return 0;
    }

    if (nowSeconds() - issuedAt >= config.memorySeconds) {
      clearStoredDeadline();
      return 0;
    }

    return deadline;
  }

  function writeStoredDeadline(deadline) {
    try {
      window.localStorage.setItem(DEADLINE_KEY, deadline + "|" + nowSeconds());
    } catch (error) {
      /* private mode: the offer still runs, it just becomes restartable */
    }
  }

  function clearStoredDeadline() {
    try {
      window.localStorage.removeItem(DEADLINE_KEY);
    } catch (error) {
      /* nothing to do, and nothing worth failing over */
    }
  }

  /**
   * product_type comes back from /cart.js in the ACTIVE LOCALE, which is why
   * the config carries every published translation rather than one German
   * string. A single-language list is how the BF campaign silently did nothing
   * in English for a fortnight.
   */
  function cartHasNora(items, config) {
    return items.some(function (item) {
      var type = (item.product_type || "").trim().toLowerCase();
      return config.types.indexOf(type) !== -1;
    });
  }

  function codeEntries(cart) {
    return cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
  }

  /**
   * Codes to carry over into the next write.
   *
   * /cart/update.js REPLACES the whole discount list instead of appending to
   * it, so anything the customer applied has to travel with every request or
   * it silently disappears — the same reason _kc-cart-discount.liquid
   * resubmits its applied codes. Only applicable ones are kept, matching what
   * that snippet does: a code Shopify has already refused would only be
   * refused again.
   */
  function keepCodes(cart, config) {
    return codeEntries(cart)
      .filter(function (entry) {
        return entry && entry.applicable && entry.code;
      })
      .map(function (entry) {
        return entry.code;
      })
      .filter(function (code) {
        return code.toLowerCase() !== config.codeKey;
      });
  }

  /**
   * Is our code already on the cart — applicable or not?
   *
   * Deliberately NOT "is it applying". A code Shopify does not recognise still
   * comes back on the cart with `applicable: false`, and treating that as
   * absent would mean re-sending it on every single cart event. Until the
   * discount is created in the admin that is exactly the state, so this
   * distinction is what keeps the theme quiet in the meantime.
   */
  function codeIsOnCart(cart, config) {
    return codeEntries(cart).some(function (entry) {
      return entry && entry.code && entry.code.toLowerCase() === config.codeKey;
    });
  }

  /**
   * One write for both facts.
   *
   * The code and the deadline travel in the SAME /cart/update.js call on
   * purpose: they are one decision, and splitting them would spend two of the
   * three writes the ceiling allows — and leave a window where the cart has a
   * deadline but no code, or the reverse.
   *
   * `deadline` may be null to leave the attribute untouched, or "" to clear it.
   */
  async function postCart(codes, deadline) {
    var formData = new FormData();
    // An empty string clears every code, which is what removal relies on.
    formData.append("discount", codes.join(","));

    if (deadline !== null && deadline !== undefined) {
      formData.append("attributes[" + DEADLINE_ATTR + "]", String(deadline));
    }

    var response = await fetch(routeRoot() + "cart/update.js", {
      method: "POST",
      body: formData,
    });

    if (!response.ok) throw new Error("discount update failed: " + response.status);
    return response.json();
  }

  /**
   * One pass: read the cart, decide, write at most once.
   *
   * The verdict is taken from a fetched /cart.js and never from the rendered
   * DOM. Liquid's answer is the cart as it was one render ago, and the drawer
   * holds its innerHTML swap for over a second — acting on that is how the BF
   * campaign used to leave an orphaned gift sitting in the cart until reload.
   */
  async function sync() {
    var config = readConfig();
    if (!config) return;

    var response = await fetch(routeRoot() + "cart.js", {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return;

    var cart = await response.json();
    var items = cart.items || [];

    var hasNora = cartHasNora(items, config);
    var onCart = codeIsOnCart(cart, config);

    if (lastHasNora !== null && lastHasNora !== hasNora) writes = 0;
    lastHasNora = hasNora;

    // No sofa means the campaign is over for this cart, so the customer's
    // earlier refusal is forgotten too: adding one again should offer it again
    // rather than stay silent for the rest of the session.
    if (!hasNora) setDismissed(false);

    // The stored record wins over the cart's. Emptying the cart takes the
    // attribute with it, and if that were the only record the next add would
    // hand out a brand-new 24 hours — the deadline has to be the one this
    // browser was already given, not the one this cart happens to carry.
    var storedDeadline = readStoredDeadline(config);
    var cartDeadline = readCartDeadline(cart);
    // The cart can now ARRIVE carrying a deadline from another device: a
    // "Warenkorb speichern" link restores it (kc-save-cart.js), so opening the
    // link on the laptop keeps the time the customer had left on their phone
    // instead of handing out a fresh 24 hours (Ivan 16/09).
    //
    // Two guards make that safe. A cart-carried deadline is capped at one
    // window from now — the link is a URL anyone can edit, and a hand-typed
    // year-3000 deadline must not buy a longer offer. And when this browser
    // already holds one, the EARLIER of the two wins: both belong to the same
    // customer's single window, and the rule is that it can shrink to the
    // truth but never grow.
    if (cartDeadline > 0) {
      cartDeadline = Math.min(cartDeadline, nowSeconds() + config.windowSeconds);
    }
    var deadline =
      storedDeadline > 0 && cartDeadline > 0
        ? Math.min(storedDeadline, cartDeadline)
        : storedDeadline || cartDeadline;
    var expired = deadline > 0 && nowSeconds() >= deadline;

    var next;
    var applying;
    var nextDeadline = null;

    if (!hasNora) {
      // The sofa left. Take the code off, but LEAVE both records alone: the
      // stored deadline is what stops a re-add from restarting the clock, and
      // the timer is gated on a sofa being present anyway, so a lingering
      // attribute shows nothing.
      if (!onCart) return;
      next = keepCodes(cart, config);
      applying = false;
    } else if (expired) {
      // The window closed. Taking the code back off is the honest end of a
      // countdown that told the customer it would run out — leaving it applied
      // would make the timer a decoration.
      //
      // The cart attribute is still refreshed to the expired value when it has
      // drifted, so Liquid agrees with us that there is nothing left to show.
      if (cartDeadline !== deadline) nextDeadline = deadline;
      if (!onCart && nextDeadline === null) return;
      next = keepCodes(cart, config);
      applying = false;
    } else {
      // A live offer. Start the clock only if this browser has never been
      // given one; otherwise adopt what it already holds.
      if (!deadline) {
        deadline = nowSeconds() + config.windowSeconds;
        writeStoredDeadline(deadline);
      } else if (deadline !== storedDeadline) {
        // Came from the cart — alone, or earlier than what this browser held
        // (a restored link). Persist it so it survives the next empty.
        writeStoredDeadline(deadline);
      }

      // Compared against the RAW attribute, not the capped value above: a
      // capped or superseded attribute has to be rewritten too, or Liquid would
      // keep rendering the banner from a deadline the script has rejected.
      if (readCartDeadline(cart) !== deadline) nextDeadline = deadline;

      if (isDismissed() || standDown) {
        // Nothing to apply, but the deadline may still need mirroring.
        if (nextDeadline === null) return;
        next = keepCodes(cart, config);
        applying = false;
      } else if (!onCart) {
        next = keepCodes(cart, config).concat([config.code]);
        applying = true;
      } else if (nextDeadline !== null) {
        next = keepCodes(cart, config).concat([config.code]);
        applying = false;
      } else {
        return;
      }
    }

    if (writes >= MAX_WRITES) {
      console.warn("[nora-promo] cart write ceiling reached, standing down");
      return;
    }
    writes++;

    var updated = await postCart(next, nextDeadline);

    // Did the code actually take? A code Shopify does not recognise normally
    // comes back on the cart as `applicable: false`, which is enough to stop
    // the next pass re-sending it. Some responses drop it entirely instead —
    // and that is the case that would otherwise retry forever.
    if (applying && !codeIsOnCart(updated, config)) {
      standDown = true;
      console.warn(
        "[nora-promo] " + config.code + " did not stick — is the discount created? standing down"
      );
      return;
    }

    // Only now: the cart really did change, and the drawer/cart page are
    // showing the previous one.
    document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
  }

  /** A pass is in flight; a second one would write the same change twice. */
  var busy = false;
  /** A pass asked for mid-flight is queued, not dropped. */
  var queued = false;

  function run() {
    if (busy) {
      queued = true;
      return;
    }

    busy = true;
    sync()
      .catch(function (error) {
        console.error("[nora-promo] sync failed:", error);
      })
      .then(release, release);
  }

  function release() {
    // A settle before the next pass: /cart.js read immediately after a write
    // can still answer with the pre-write cart, and acting on that answer is
    // how a code gets applied twice.
    setTimeout(function () {
      busy = false;
      if (!queued) return;
      queued = false;
      run();
    }, 400);
  }

  /**
   * The customer taking the code off is a decision, not an error to correct.
   *
   * Without this the removal button in _kc-cart-discount.liquid is a button
   * that does nothing: it clears the code, the resulting cart:refresh brings us
   * here, the sofa is still in the cart and we put it straight back.
   *
   * CAPTURE PHASE, and that is not a style choice. <cart-drawer> and <cart-mini>
   * handle this same button with a delegated listener that calls
   * stopPropagation() (assets/cart-drawer.js, assets/cart.js), so a bubble-phase
   * listener on document is never reached. Capture runs on the way down, before
   * theirs.
   */
  document.addEventListener(
    "click",
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== "function") return;

      var button = target.closest("[data-remove-discount]");
      if (!button) return;

      var config = readConfig();
      if (!config) return;

      var removed = (button.getAttribute("data-remove-discount") || "").trim().toLowerCase();
      if (removed !== config.codeKey) return;

      setDismissed(true);
    },
    true
  );

  /* ── The countdown ────────────────────────────────────────────────────────
   *
   * Ticks whatever [data-kc-nora-countdown] nodes are on the page. The cart
   * re-renders its own innerHTML, so the nodes are looked up on every tick
   * rather than held: a reference captured once points at a node that has been
   * thrown away, and the clock silently stops.
   *
   * ONE interval for the whole page, started only when there is something to
   * count. The drawer and /cart never coexist, but the drawer alone can hold
   * more than one instance across re-renders.
   */
  var ticker = null;

  function pad(value) {
    return value < 10 ? "0" + value : String(value);
  }

  function tick() {
    var nodes = document.querySelectorAll("[data-kc-nora-countdown]");
    if (!nodes.length) {
      clearInterval(ticker);
      ticker = null;
      return;
    }

    var now = nowSeconds();
    var anyLive = false;
    // The ceiling on what a clock may show: one window from now. The banner's
    // deadline is rendered by Liquid straight from the cart attribute, and a
    // restored "Warenkorb speichern" link writes that attribute BEFORE this
    // script has had the chance to cap it (sync does, a moment later) — so a
    // hand-edited link painted a ten-day countdown on /cart until the next
    // reload. Capping here makes the display honest whatever wrote the value.
    var tickConfig = readConfig();
    var ceiling = tickConfig ? now + tickConfig.windowSeconds : 0;

    Array.prototype.forEach.call(nodes, function (node) {
      var deadline = parseInt(node.getAttribute("data-deadline"), 10);
      if (!(deadline > 0)) return;
      if (ceiling && deadline > ceiling) {
        deadline = ceiling;
        node.setAttribute("data-deadline", String(deadline));
      }

      var left = deadline - now;

      if (left <= 0) {
        // Reaching zero is a cart change, not just a display change: sync()
        // takes the code back off. Marked first so the re-render this triggers
        // cannot bounce us in here twice.
        if (node.getAttribute("data-expired") === "1") return;
        node.setAttribute("data-expired", "1");
        node.textContent = node.getAttribute("data-expired-label") || "";
        run();
        // the side tab has no "expired" state worth showing: it just leaves
        if (node.hasAttribute("data-kc-nora-tab-clock")) hideTab();
        return;
      }

      anyLive = true;

      // Past a day, hours alone stop reading as a duration: "71:04:22" is a
      // number, "2T 23:04:22" is three days. The unit comes from the markup so
      // it stays translatable — the JS must not decide what a day is called.
      var days = Math.floor(left / 86400);
      var hours = Math.floor((left % 86400) / 3600);
      var minutes = Math.floor((left % 3600) / 60);
      var seconds = left % 60;

      var clock = pad(hours) + ":" + pad(minutes) + ":" + pad(seconds);

      if (days > 0) {
        var dayUnit = node.getAttribute("data-days-unit") || "d";
        clock = days + dayUnit + " " + clock;
      }

      node.textContent = clock;
    });

    if (!anyLive) {
      clearInterval(ticker);
      ticker = null;
    }
  }

  function startTicker() {
    if (ticker) return;
    if (!document.querySelector("[data-kc-nora-countdown]")) return;
    tick();
    ticker = setInterval(tick, 1000);
  }

  // cart:change is the theme's add-to-cart; cart:refresh is the "reread
  // everything" signal. The delay lets the write that triggered the event
  // land — /cart.js answered any sooner still describes the previous cart.
  ["cart:change", "cart:refresh"].forEach(function (name) {
    document.addEventListener(name, function () {
      setTimeout(run, 250);
      // The re-render replaced the countdown node, so re-arm against the new
      // one. Cheap: startTicker is a no-op when a ticker is already running.
      setTimeout(startTicker, 300);
    });
  });

  /**
   * The banner can appear LATER than any fixed delay we could pick.
   *
   * The sequence that exposed it: adding the sofa fires cart:change, the
   * re-arm above runs 300 ms later — but at that point sync() has not written
   * the deadline yet, so Liquid rendered no banner and there is nothing to
   * start the clock on. sync() then stamps the deadline and dispatches
   * cart:refresh; the drawer re-renders, and if that render lands after the
   * next 300 ms re-arm, startTicker has already come and gone. No further cart
   * event ever fires, so the clock never starts and the banner sits there with
   * its time blank — which is exactly what the customer sees (Ivan 16/09: "no
   * sale el timer").
   *
   * So: watch for the node instead of guessing when it arrives. The callback
   * is cheap — once a ticker exists it returns on the first line, and tick()
   * itself stops the interval when the nodes go away, which re-arms this.
   */
  function watchForCountdown() {
    if (typeof MutationObserver !== "function") return;
    new MutationObserver(function () {
      if (ticker) return;
      startTicker();
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ── The "don't forget" popup and its side tab ───────────────────────────
   *
   * The popup opens when the cart drawer CLOSES with the offer still running
   * (Ivan 16/09). Once per session: a reminder, not a gate — reopening and
   * closing the drawer must not keep throwing it back up.
   *
   * After that, a small tab on the left edge keeps the countdown in view on
   * every page for as long as the sofa is in the cart; tapping it opens the
   * popup again, whenever the customer wants.
   *
   * Both only appear when all of these hold, checked against a FRESH /cart.js
   * (readOffer):
   *   - the popup is switched on (its markup exists at all, nora[10])
   *   - a qualifying sofa is in the cart
   *   - Shopify is really applying the code (applicable: true) — unless the
   *     TESTING switch nora_popup_require_applied = false lifts that
   *   - the deadline is still running
   *   - the customer has not taken the code off themselves
   * and the popup, additionally, never while they are heading to checkout.
   */
  var POPUP_SHOWN_KEY = "kc:nora-popup-shown";
  var leavingForCheckout = false;

  function popupShownThisSession() {
    try {
      return window.sessionStorage.getItem(POPUP_SHOWN_KEY) === "1";
    } catch (error) {
      return false;
    }
  }

  function markPopupShown() {
    try {
      window.sessionStorage.setItem(POPUP_SHOWN_KEY, "1");
    } catch (error) {
      /* private mode: worst case it can show again on the next page */
    }
  }

  // The drawer's own checkout button closes nothing, but a close that races a
  // navigation would flash the popup over the checkout redirect.
  document.addEventListener(
    "click",
    function (event) {
      var target = event.target;
      if (target && target.closest && target.closest('[name="checkout"], [data-kc-nora-popup-checkout]')) {
        leavingForCheckout = true;
      }
    },
    true
  );

  function closePopup(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.close();
  }

  /** /cart.js hands back the ORIGINAL upload; ask the CDN for a sensible size. */
  function sizedImage(url, width) {
    try {
      var sized = new URL(url, window.location.origin);
      sized.searchParams.set("width", String(width));
      return sized.toString();
    } catch (error) {
      return url;
    }
  }

  function noop() {}

  /**
   * What this cart can be offered right now, from a fresh /cart.js — or null
   * when there is nothing to offer. The one gate both the popup and the tab
   * go through, so they can never disagree about whether the offer stands.
   *
   * The sofa is the CART LINE, not the product: its `image` is the variant's
   * own picture, so the popup shows the colour the customer actually chose.
   */
  async function readOffer(dialog) {
    var config = readConfig();
    if (!config || isDismissed()) return null;

    var response = await fetch(routeRoot() + "cart.js", {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return null;
    var cart = await response.json();
    var items = cart.items || [];

    var sofa = null;
    for (var i = 0; i < items.length; i++) {
      var type = (items[i].product_type || "").trim().toLowerCase();
      if (config.types.indexOf(type) !== -1) {
        sofa = items[i];
        break;
      }
    }
    if (!sofa) return null;

    var applying = codeEntries(cart).some(function (entry) {
      return entry && entry.applicable && entry.code && entry.code.toLowerCase() === config.codeKey;
    });
    var requireApplied = dialog.getAttribute("data-require-applied") !== "false";
    if (requireApplied && !applying) return null;

    // same rule as sync(): a cart-carried deadline can shorten the window,
    // never extend it
    var stored = readStoredDeadline(config);
    var carried = readCartDeadline(cart);
    if (carried > 0) carried = Math.min(carried, nowSeconds() + config.windowSeconds);
    var deadline = stored > 0 && carried > 0 ? Math.min(stored, carried) : stored || carried;
    if (!(deadline > nowSeconds())) return null;

    return { sofa: sofa, deadline: deadline };
  }

  function armClock(node, deadline) {
    if (!node) return;
    node.removeAttribute("data-expired");
    node.setAttribute("data-deadline", String(deadline));
    node.setAttribute("data-kc-nora-countdown", "");
  }

  function disarmClock(node) {
    if (node) node.removeAttribute("data-kc-nora-countdown");
  }

  function openPopup(dialog, offer) {
    hideTab();

    var sofa = offer.sofa;
    var product = dialog.querySelector("[data-kc-nora-popup-product]");
    var media = dialog.querySelector("[data-kc-nora-popup-media]");
    var name = dialog.querySelector("[data-kc-nora-popup-name]");
    if (product && media) {
      if (sofa.image) {
        media.src = sizedImage(sofa.image, 920);
        media.alt = sofa.product_title || "";
        product.hidden = false;
      } else {
        product.hidden = true;
      }
    }
    if (name) name.textContent = sofa.product_title || sofa.title || "";

    var clock = dialog.querySelector("[data-kc-nora-popup-clock]");
    if (clock) clock.textContent = "";
    armClock(clock, offer.deadline);

    markPopupShown();
    dialog.showModal();
    // showModal() focuses the first control — the close button — and paints a
    // focus ring on it before anyone has touched the keyboard. Focus the dialog
    // itself instead: screen readers still announce it by its title, and Tab
    // still walks into the button.
    dialog.focus({ preventScroll: true });
    // attribute changes are not childList mutations: arm the clock ourselves
    tick();
    startTicker();
  }

  /** The drawer just closed: the automatic, once-per-session opening. */
  async function maybeShowPopup() {
    var dialog = document.querySelector("[data-kc-nora-popup]");
    if (!dialog || dialog.open) return;
    if (leavingForCheckout || popupShownThisSession()) return;
    // another dialog took over (search, quick buy…): don't stack on top of it
    if (document.querySelector("dialog[open]")) return;

    var offer = await readOffer(dialog);
    if (!offer) return;

    // the checks awaited a network call — re-check what could have changed
    if (leavingForCheckout || dialog.open || document.querySelector("dialog[open]")) return;
    openPopup(dialog, offer);
  }

  function hideTab() {
    var tab = document.querySelector("[data-kc-nora-tab]");
    if (!tab) return;
    tab.hidden = true;
    disarmClock(tab.querySelector("[data-kc-nora-tab-clock]"));
  }

  /** A later check supersedes an earlier one still waiting on the network. */
  var tabCheck = 0;

  async function refreshTab() {
    var tab = document.querySelector("[data-kc-nora-tab]");
    var dialog = document.querySelector("[data-kc-nora-popup]");
    if (!tab || !dialog) return;
    // the tab belongs to AFTER the popup: never before it has been seen, and
    // never alongside it
    if (dialog.open || !popupShownThisSession()) {
      hideTab();
      return;
    }

    var check = ++tabCheck;
    var offer = await readOffer(dialog);
    if (check !== tabCheck) return;
    if (!offer || dialog.open) {
      hideTab();
      return;
    }

    armClock(tab.querySelector("[data-kc-nora-tab-clock]"), offer.deadline);
    tab.hidden = false;
    tick();
    startTicker();
  }

  /** The tab was tapped: the popup again, whenever the customer asks for it. */
  async function openFromTab() {
    var dialog = document.querySelector("[data-kc-nora-popup]");
    if (!dialog || dialog.open) return;
    var offer = await readOffer(dialog);
    if (!offer) {
      hideTab();
      return;
    }
    if (dialog.open) return;
    openPopup(dialog, offer);
  }

  function wirePopup() {
    // All DELEGATED at the document, none bound to the dialog or the tab: if
    // the section holding them is ever re-rendered, listeners on the old nodes
    // would leave the new ones dead.
    document.addEventListener("click", function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      if (target.closest("[data-kc-nora-tab]")) {
        openFromTab().catch(function (error) {
          console.error("[nora-promo] tab failed:", error);
        });
        return;
      }

      var closer = target.closest("[data-kc-nora-popup-close]");
      if (closer) {
        closePopup(closer.closest("[data-kc-nora-popup]"));
        return;
      }

      // A click whose target is the <dialog> itself is either its backdrop OR
      // its own padding — tell them apart by position, or tapping the white
      // space inside the card would dismiss it.
      if (target.matches && target.matches("[data-kc-nora-popup]") && target.open) {
        var rect = target.getBoundingClientRect();
        var inside =
          event.clientX >= rect.left && event.clientX <= rect.right &&
          event.clientY >= rect.top && event.clientY <= rect.bottom;
        if (!inside) closePopup(target);
      }
    });

    // `close` does not bubble; a capturing listener still sees it. Closing the
    // popup is exactly when the tab takes over.
    document.addEventListener(
      "close",
      function (event) {
        var target = event.target;
        if (!target || !target.matches || !target.matches("[data-kc-nora-popup]")) return;
        disarmClock(target.querySelector("[data-kc-nora-popup-clock]"));
        refreshTab().catch(noop);
      },
      true
    );

    // The theme's drawers (Impact DialogElement) announce the end of their
    // closing animation with dialog:after-hide. Captured at the document: the
    // drawer's markup is replaced on every cart change, and a listener bound
    // to it would be thrown away with it. readOffer itself checks whether the
    // popup is switched on (its markup present).
    document.addEventListener(
      "dialog:after-hide",
      function (event) {
        var target = event.target;
        if (!target || target.tagName !== "CART-DRAWER") return;
        maybeShowPopup().catch(function (error) {
          console.error("[nora-promo] popup failed:", error);
        });
      },
      true
    );

    // The sofa leaving the cart (or the code coming off) takes the tab with it.
    // After sync() has had its turn to write, hence the delay.
    ["cart:change", "cart:refresh"].forEach(function (name) {
      document.addEventListener(name, function () {
        setTimeout(function () {
          refreshTab().catch(noop);
        }, 900);
      });
    });

    // every page of the session after the popup was seen starts with the tab
    refreshTab().catch(noop);
  }

  function boot() {
    run();
    startTicker();
    watchForCountdown();
    wirePopup();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
